import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeftRight,
  CalendarDays,
  Users,
  Minus,
  Plus,
  Info,
  Plane,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { AIRPORTS, ASSETS, SPECIAL_FARES, searchAirports } from "@/mock";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { apiFareCalendar, apiSearchAirports } from "@/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ---------- Airport Select ----------
const AirportSelect = ({ label, value, onChange, forceOpen = 0 }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [nearCity, setNearCity] = useState("");
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  useEffect(() => { if (forceOpen > 0) { setQuery(""); setOpen(true); } }, [forceOpen]);
  const handleOpen = (o) => { setOpen(o); if (o) setQuery(value.city || ""); };

  // Debounced live search against the official TripJack airport list (backend).
  // Falls back to the local bundled list if the API is unreachable.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await apiSearchAirports(query, 25);
        setResults(r.airports || []);
        setNearby(r.nearby || []);
        setNearCity(r.near_city || "");
      } catch {
        setResults(searchAirports(query, 25));
        setNearby([]);
        setNearCity("");
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [query, open]);

  const filtered = results;

  const renderOption = (a, extra = "") => (
    <button
      key={a.code + a.city + extra}
      onClick={() => {
        onChange(a);
        setOpen(false);
        setQuery("");
      }}
      className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[#1B1F5B]/5 disabled:opacity-40 ${
        value.code === a.code ? "bg-[#1B1F5B]/5" : ""
      }`}
    >
      {a.country === "India" ? (
        <img src={ASSETS.indiaFlag} alt="" className="mt-1 h-3.5 w-5 rounded-[2px] object-cover" />
      ) : (
        <Plane className="mt-1 h-4 w-4 text-slate-300" />
      )}
      <span className="flex-1">
        <span className="block text-sm font-semibold text-slate-800">
          {a.code} - {a.city}, {a.country}
        </span>
        <span className="block text-xs text-slate-400">{a.name}</span>
      </span>
      {a.distance_km != null && (
        <span className="ml-auto mt-1 shrink-0 rounded-full bg-[#0E7C6B]/10 px-2 py-0.5 text-[10px] font-bold text-[#0E7C6B]">
          ~{a.distance_km} km
        </span>
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`airport-field-${label.toLowerCase()}`}
          className="group flex w-full flex-col items-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left transition-colors duration-200 hover:border-[#1B1F5B]/40"
        >
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {label}
          </span>
          <span className="mt-0.5 text-[17px] font-bold leading-tight text-slate-800">
            {value.code ? `${value.code} - ${value.city}` : <span className="text-slate-400">Select city</span>}
          </span>
          <span className="mt-0.5 w-full truncate text-xs text-slate-400">
            {value.name || "\u00a0"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[340px] overflow-hidden border-slate-200 bg-white p-0 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.2)]"
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
      >
        <div className="border-b border-slate-100 bg-slate-50/60 p-3">
          <input
            autoFocus
            data-testid={`airport-search-${label.toLowerCase()}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Search city, airport or country"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1.5" data-testid={`airport-results-${label.toLowerCase()}`}>
          {filtered.map((a) => renderOption(a))}
          {nearby.length > 0 && (
            <>
              <p className="mt-1 border-t border-slate-100 px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-400" data-testid={`airport-nearby-head-${label.toLowerCase()}`}>
                Airports near {nearCity}
              </p>
              <div data-testid={`airport-nearby-${label.toLowerCase()}`}>
                {nearby.map((a) => renderOption(a, "near"))}
              </div>
            </>
          )}
          {filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate-400">
              {searching ? "Searching airports..." : "No airport found"}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ---------- Date Field (with MMT-style per-day fare calendar) ----------
const DateField = ({ label, date, onSelect, placeholder, minDate, fares, lowest, faresLoading, onMonthChange, dayPrefix = "fare-day", forceOpen = 0 }) => {
  const [open, setOpen] = useState(false);
  // Auto-open (e.g. after departure picked in round-trip, return calendar pops up)
  useEffect(() => { if (forceOpen > 0) setOpen(true); }, [forceOpen]);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) onMonthChange?.(date || new Date()); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full flex-col items-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left transition-colors duration-200 hover:border-[#1B1F5B]/40"
        >
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {label}
          </span>
          {date ? (
            <>
              <span className="mt-0.5 text-[17px] font-bold leading-tight text-slate-800">
                {format(date, "dd MMM''yy")}
              </span>
              <span className="mt-0.5 text-xs text-slate-400">
                {format(date, "EEEE")}
              </span>
            </>
          ) : (
            <>
              <span className="mt-0.5 flex items-center gap-1.5 text-[15px] font-medium leading-tight text-slate-400">
                <CalendarDays className="h-4 w-4" /> {placeholder}
              </span>
              <span className="mt-0.5 text-xs text-slate-300">&nbsp;</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[75vh] w-auto overflow-y-auto border-slate-200 bg-white p-0 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.2)]"
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
      >
        {faresLoading && (
          <p className="border-b border-slate-100 px-3 py-1.5 text-center text-[10px] font-bold text-[#0E7C6B]" data-testid="fare-calendar-loading">Loading lowest fares…</p>
        )}
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date || minDate || new Date()}
          showOutsideDays={false}
          onSelect={(d) => {
            onSelect(d);
            setOpen(false);
          }}
          disabled={(d) => d < (minDate || new Date(new Date().setHours(0, 0, 0, 0)))}
          onMonthChange={(m) => onMonthChange?.(m)}
          initialFocus
          classNames={{
            head_cell: "text-muted-foreground rounded-md w-11 font-normal text-[0.7rem]",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-md [&:has([aria-selected])]:bg-accent w-11",
            day: "h-12 w-11 p-0 font-normal aria-selected:opacity-100",
          }}
          components={{
            IconLeft: ({ className, ...p }) => <ChevronLeft className={cn("h-4 w-4", className)} {...p} />,
            IconRight: ({ className, ...p }) => <ChevronRight className={cn("h-4 w-4", className)} {...p} />,
            DayContent: ({ date: d }) => {
              const key = format(d, "yyyy-MM-dd");
              const f = fares?.[key];
              return (
                <div className="flex flex-col items-center leading-none" data-testid={f != null ? `${dayPrefix}-${key}` : undefined}>
                  <span className="text-[13px]">{d.getDate()}</span>
                  {f != null ? (
                    <span className={`mt-1 text-[9px] font-extrabold ${lowest === key ? "text-emerald-500" : "text-slate-400"}`}>
                      ₹{f >= 1000 ? `${(f / 1000).toFixed(1)}k` : f}
                    </span>
                  ) : (
                    <span className="mt-1 text-[9px] text-slate-200">{faresLoading ? "…" : " "}</span>
                  )}
                </div>
              );
            },
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

// ---------- Traveller Counter ----------
const Counter = ({ label, sub, value, onChange, min, max }) => (
  <div className="flex items-center justify-between py-2.5">
    <div>
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      <p className="text-xs text-slate-400">{sub}</p>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#1B1F5B] hover:text-[#1B1F5B] disabled:opacity-30"
        disabled={value <= min}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-4 text-center text-sm font-bold text-slate-800">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#1B1F5B] hover:text-[#1B1F5B] disabled:opacity-30"
        disabled={value >= max}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

const CLASSES = ["Economy", "Premium Economy", "Business", "First Class"];

// ---------- Main Widget ----------
const FlightSearchWidget = ({ compact = false, onSearched, initial = null }) => {
  const navigate = useNavigate();
  const [tripType, setTripType] = useState(initial?.tripType || "one-way");
  const [from, setFrom] = useState(initial?.from || AIRPORTS.find((a) => a.code === "BOM"));
  const [to, setTo] = useState(initial?.to || AIRPORTS.find((a) => a.code === "DEL"));
  const [depart, setDepart] = useState(() => initial?.depart || new Date(Date.now() + 864e5)); // default tomorrow (same-day late-night searches are rejected by TripJack)
  const [ret, setRet] = useState(initial?.ret || null);
  const [travOpen, setTravOpen] = useState(false);
  const [pax, setPax] = useState(initial?.pax || { adults: 1, children: 0, infants: 0 });
  const [cabin, setCabin] = useState(initial?.cabin || "Economy");
  const [retAutoOpen, setRetAutoOpen] = useState(0);
  const [fare, setFare] = useState("regular");
  const [recent, setRecent] = useState([]);
  const [fareMap, setFareMap] = useState({});
  const [fareLoading, setFareLoading] = useState(false);
  const fareLoadedRef = useRef(new Set());

  // Lowest fare across all loaded months (for the green highlight)
  const fareLowest = useMemo(() => {
    const entries = Object.entries(fareMap);
    if (!entries.length) return "";
    return entries.reduce((a, b) => (b[1] < a[1] ? b : a))[0];
  }, [fareMap]);

  // Fetch per-day lowest fares for the visible month (cached per route+month)
  const loadMonthFares = async (d) => {
    if (!from?.code || !to?.code || from.code === to.code) return;
    const month = format(d, "yyyy-MM");
    const key = `${from.code}-${to.code}-${month}`;
    if (fareLoadedRef.current.has(key)) return;
    fareLoadedRef.current.add(key);
    setFareLoading(true);
    try {
      const r = await apiFareCalendar(from.code, to.code, month);
      if (r?.fares) setFareMap((m) => ({ ...m, ...r.fares }));
    } catch {
      fareLoadedRef.current.delete(key);
    } finally {
      setFareLoading(false);
    }
  };

  // Route change -> clear stale fares
  useEffect(() => {
    setFareMap({});
  }, [from?.code, to?.code]);

  useEffect(() => {
    try {
      setRecent(JSON.parse(localStorage.getItem("ft_recent") || "[]"));
    } catch {
      setRecent([]);
    }
  }, []);

  const totalPax = pax.adults + pax.children + pax.infants;

  const EMPTY = { code: "", city: "", name: "" };
  const [toOpen, setToOpen] = useState(0);
  const pickFrom = (a) => {
    setFrom(a);
    if (a.code === to.code || !to.code) { setTo(EMPTY); setToOpen((c) => c + 1); }
  };
  const pickTo = (a) => {
    setTo(a);
    if (a.code === from.code) setFrom(EMPTY);
  };

  const swap = () => {
    const f = from;
    setFrom(to);
    setTo(f);
  };

  const doSearch = () => {
    if (!from.code || !to.code) {
      toast.error("Please select both From and To cities");
      return;
    }
    if (from.code === to.code) {
      toast.error("Origin and destination cannot be the same");
      return;
    }
    const params = new URLSearchParams({
      tripType,
      from: from.code,
      fromCity: from.city,
      to: to.code,
      toCity: to.city,
      dep: format(depart, "yyyy-MM-dd"),
      ret: tripType === "round" && ret ? format(ret, "yyyy-MM-dd") : "",
      ADT: pax.adults,
      CHD: pax.children,
      INF: pax.infants,
      cabin,
      fare,
    });
    const item = {
      from: from.city,
      to: to.city,
      date: format(depart, "dd-MM-yyyy"),
    };
    const list = [item, ...recent.filter((r) => !(r.from === item.from && r.to === item.to))].slice(0, 4);
    localStorage.setItem("ft_recent", JSON.stringify(list));
    setRecent(list);
    if (onSearched) onSearched(params);
    else navigate(`/flight/search?${params.toString()}`);
  };

  return (
    <div className="w-full">
      {/* Trip type + group fares */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-5">
          {[
            { id: "one-way", label: "One Way" },
            { id: "round", label: "Round Trip" },
          ].map((t) => (
            <label key={t.id} className="flex cursor-pointer items-center gap-2">
              <span
                onClick={() => setTripType(t.id)}
                className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 transition-colors ${
                  tripType === t.id ? "border-[#1B1F5B]" : "border-slate-300"
                }`}
              >
                {tripType === t.id && (
                  <span className="h-2.5 w-2.5 rounded-full bg-[#1B1F5B]" />
                )}
              </span>
              <span
                className={`text-sm font-semibold ${
                  tripType === t.id ? "text-[#1B1F5B]" : "text-slate-500"
                }`}
              >
                {t.label}
              </span>
            </label>
          ))}
        </div>
        <button
          onClick={() => navigate("/group-enquiry")}
          className="flex items-center gap-2 text-[13px] font-semibold text-slate-700"
        >
          <Users className="h-4 w-4 text-[#1B1F5B]" />
          More than 9 Pax ?
          <span className="rounded-full bg-gradient-to-r from-[#F5B32B] to-[#E89A10] px-3 py-1 text-xs font-bold text-white shadow-sm transition-transform duration-200 hover:scale-[1.03]">
            Get Exclusive Group Fares
          </span>
        </button>
      </div>

      {/* Fields */}
      <div className="rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-[0_15px_50px_rgba(27,31,91,0.12)] backdrop-blur">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_0.9fr_0.9fr_1fr_auto]">
          <div className="relative">
            <AirportSelect label="From" value={from} onChange={pickFrom} />
            <button
              type="button"
              onClick={swap}
              aria-label="Swap"
              data-testid="swap-cities-mobile"
              className="absolute bottom-0 right-5 z-10 flex h-9 w-9 translate-y-1/2 items-center justify-center rounded-full bg-[#1B1F5B] text-white shadow-md transition-transform duration-300 active:rotate-180 md:bottom-auto md:right-0 md:top-1/2 md:translate-x-1/2 md:-translate-y-1/2 lg:hidden"
            >
              <ArrowLeftRight className="h-4 w-4 rotate-90 md:rotate-0" />
            </button>
          </div>
          <div className="relative hidden lg:block">
            <button
              type="button"
              onClick={swap}
              aria-label="Swap"
              data-testid="swap-cities"
              className="absolute left-1/2 top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#1B1F5B] text-white shadow-md transition-transform duration-300 hover:rotate-180"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
          </div>
          <AirportSelect label="To" value={to} onChange={pickTo} forceOpen={toOpen} />
          <DateField label="Departure" date={depart} onSelect={(d) => { setDepart(d); if (d && tripType === "round" && !ret) setRetAutoOpen((c) => c + 1); }} placeholder="Departure"
            fares={fareMap} lowest={fareLowest} faresLoading={fareLoading} onMonthChange={loadMonthFares} />
          {tripType === "round" ? (
            <DateField
              label="Return"
              date={ret}
              onSelect={setRet}
              placeholder="Tap to add return date"
              minDate={depart}
              fares={fareMap}
              lowest={fareLowest}
              faresLoading={fareLoading}
              onMonthChange={loadMonthFares}
              dayPrefix="fare-day-return"
              forceOpen={retAutoOpen}
            />
          ) : (
            <button
              type="button"
              onClick={() => setTripType("round")}
              className="flex w-full flex-col items-start rounded-xl border border-dashed border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:border-[#1B1F5B]/40"
            >
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Return
              </span>
              <span className="mt-0.5 text-[13px] font-medium leading-tight text-slate-400">
                Tap to add return date
              </span>
              <span className="mt-0.5 text-xs text-slate-300">&nbsp;</span>
            </button>
          )}

          {/* Travellers */}
          <Popover open={travOpen} onOpenChange={setTravOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex w-full flex-col items-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left transition-colors duration-200 hover:border-[#1B1F5B]/40"
              >
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Traveler(s) & Class
                </span>
                <span className="mt-0.5 text-[17px] font-bold leading-tight text-slate-800">
                  {totalPax} Pax
                </span>
                <span className="mt-0.5 text-xs uppercase text-slate-400">{cabin}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="max-h-[75vh] w-[320px] overflow-y-auto border-slate-200 bg-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.2)]"
              align="end"
              side="bottom"
              sideOffset={8}
              collisionPadding={12}
            >
              <Counter
                label="Adults"
                sub="(Aged 12+ yrs)"
                value={pax.adults}
                min={1}
                max={9}
                onChange={(v) => setPax({ ...pax, adults: v })}
              />
              <Counter
                label="Children"
                sub="(Aged 2-12 yrs)"
                value={pax.children}
                min={0}
                max={6}
                onChange={(v) => setPax({ ...pax, children: v })}
              />
              <Counter
                label="Infant"
                sub="(Below 2 yrs)"
                value={pax.infants}
                min={0}
                max={pax.adults}
                onChange={(v) => setPax({ ...pax, infants: v })}
              />
              <div className="mt-2 border-t pt-3">
                <p className="mb-2 text-sm font-semibold text-slate-800">Travel Class</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {CLASSES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCabin(c)}
                      className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${
                        cabin === c
                          ? "border-[#1B1F5B] bg-[#1B1F5B] text-white"
                          : "border-slate-200 text-slate-600 hover:border-[#1B1F5B]/50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setTravOpen(false)}
                  className="rounded-lg px-4 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setTravOpen(false)}
                  className="rounded-lg bg-[#1B1F5B] px-5 py-1.5 text-sm font-semibold text-white hover:bg-[#141844]"
                >
                  Done
                </button>
              </div>
            </PopoverContent>
          </Popover>

          <button
            onClick={doSearch}
            className="flex items-center justify-center rounded-xl bg-[#1B1F5B] px-8 text-[15px] font-bold tracking-wider text-white shadow-[0_8px_24px_rgba(27,31,91,0.35)] transition-all duration-200 hover:bg-[#141844] hover:shadow-[0_10px_28px_rgba(27,31,91,0.45)] max-lg:py-3.5"
          >
            SEARCH
          </button>
        </div>

        {/* Special fares */}
        <div className="mt-3 flex flex-wrap items-center gap-2.5 border-t border-dashed border-slate-200 pt-3">
          <div className="mr-1 leading-tight">
            <span className="block rounded-md bg-[#1B1F5B] px-2 py-0.5 text-[10px] font-bold uppercase text-white">
              More Benefits
            </span>
            <span className="mt-1 block text-xs font-semibold text-slate-600">Special Fares</span>
          </div>
          {SPECIAL_FARES.map((f) => (
            <div key={f.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setFare(f.id)}
                className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2 transition-all duration-200 ${
                  fare === f.id
                    ? "border-[#1B1F5B] bg-[#1B1F5B]/[0.04] shadow-sm"
                    : "border-slate-200 hover:border-[#1B1F5B]/40"
                }`}
              >
                <span
                  className={`flex h-4.5 w-4.5 items-center justify-center rounded border-2 text-white transition-colors ${
                    fare === f.id ? "border-[#1B1F5B] bg-[#1B1F5B]" : "border-slate-300"
                  }`}
                >
                  {fare === f.id && (
                    <svg viewBox="0 0 12 12" className="h-3 w-3 fill-none stroke-current stroke-2">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </span>
                <span className="text-left leading-tight">
                  <span className="block text-[13px] font-bold text-slate-800">{f.title}</span>
                  <span className="block text-[11px] text-slate-400">{f.sub}</span>
                </span>
              </button>
              {f.info && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-slate-400 hover:text-[#1B1F5B]">
                      <Info className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-80 border-slate-200 bg-white text-xs leading-relaxed text-slate-600 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.2)]"
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    collisionPadding={12}
                  >
                    <p className="mb-1 text-sm font-bold text-slate-800">{f.title} Fare</p>
                    {f.info}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent searches */}
      {recent.length > 0 && !compact && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-semibold text-slate-500">
            Your recent <span className="text-[#1B1F5B]">searches</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {recent.map((r, i) => (
              <button
                key={i}
                onClick={() => toast.info(`${r.from} to ${r.to} - search again from the form above`)}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:border-[#1B1F5B]/40 hover:text-[#1B1F5B]"
              >
                <Plane className="h-3.5 w-3.5 text-[#1B1F5B]" />
                {r.from} → {r.to}
                <span className="text-slate-400">{r.date}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightSearchWidget;
